import { Environment } from '@/internals/env.config';
import { APP_STORE_LINK, PLAY_STORE_LINK } from '@/ui/utils/externalLinks';

import { makeSchemaTarget } from './makeSchemaTarget';

interface GenerateLinkParams {
  referer: string;
  device: DeviceType;
  linkFallback?: string;
  useSchema: boolean;
  env: Environment;
}

interface GenerateLinkResult {
  href: string;
  useWindow: boolean;
  clipboardTarget?: string;
}

export enum DeviceType {
  IOS = 'ios',
  ANDROID = 'android',
  WEB = 'web',
}

export function getDeviceType(ua: string): DeviceType {
  const lowerCaseUa = ua.toLowerCase();
  if (/(ipad|iphone|ipod|fxios)/gi.test(lowerCaseUa)) {
    return DeviceType.IOS;
  } else if (/android/gi.test(lowerCaseUa)) {
    return DeviceType.ANDROID;
  } else {
    return DeviceType.WEB;
  }
}

export function generateDownloadLink({
  env,
  device,
  referer,
  linkFallback,
  useSchema,
}: GenerateLinkParams): GenerateLinkResult {
  const isIOS = device === DeviceType.IOS;
  const isAndroid = device === DeviceType.ANDROID;

  const schemaLinkRaw = makeSchemaTarget(env, device, referer);
  const schemaFallBack = makeSchemaTarget(env, device, linkFallback);
  // Android doesn't support schema so we're using the fallback to trigger
  // a reload and have the universal link open in app
  const schemaLink = isAndroid ? schemaFallBack : schemaLinkRaw || schemaFallBack;

  let targetRaw;

  switch (device) {
    case DeviceType.ANDROID:
      targetRaw = PLAY_STORE_LINK;
      break;
    case DeviceType.IOS:
      targetRaw = APP_STORE_LINK;
      break;
    case DeviceType.WEB:
    default:
      targetRaw = makeSchemaTarget(env, DeviceType.WEB, referer || linkFallback) || env.config.APP_ORIGIN;
      break;
  }

  let targetHref: string;
  let currentUrl: URL | undefined;

  try {
    currentUrl = getMaybeUrl(referer === '' ? linkFallback || referer : referer);
    let targetUrl = new URL(targetRaw);

    targetUrl = withUtmParameters(currentUrl, targetUrl) || targetUrl;

    if (isAndroid) {
      const encodedReferrer = encodeURI(`utm_source=${currentUrl?.toString() || ''}`);
      targetUrl.searchParams.append('referrer', encodedReferrer);
    }

    targetHref = targetUrl.toString();
  } catch {
    targetHref = targetRaw;
  }

  const href = useSchema && schemaLink ? schemaLink : targetHref;
  const useWindow = isIOS && !currentUrl;
  const clipboardTarget = isIOS ? currentUrl?.toString() : undefined;

  return { href, useWindow, clipboardTarget };
}

function withUtmParameters(sourceURL: URL | undefined, targetURL: URL): URL | undefined {
  if (!sourceURL) return;

  const sourceParams = new URLSearchParams(sourceURL.search);
  const targetParams = new URLSearchParams(targetURL.search);

  for (const [key, value] of sourceParams) {
    if (key.startsWith('utm_')) {
      targetParams.set(key, value);
    }
  }

  const resultTargetUrl = new URL(targetURL);
  resultTargetUrl.search = targetParams.toString();

  return resultTargetUrl;
}

const getMaybeUrl = (url: string): URL | undefined => {
  try {
    const result = new URL(url);
    return result;
  } catch (_) {
    return undefined;
  }
};
